<template>
  <v-card class="pa-5 overflow-y-hidden d-flex flex-column">
    <v-row
      v-if="reportContextButtonKey || aiFocusAreas"
      no-gutters
      justify="end"
      class="pb-2"
    >
      <v-col class="shrink d-flex">
        <platform-button
          v-if="aiFocusAreas"
          secondary
          default-case
          icon="robot-outline"
          class="mr-2"
          @click="showAiConfirmDialog = true"
        >
          {{ $t('analysis.ai.insertContentAction') }}
        </platform-button>
        <report-context-button
          v-if="reportContextButtonKey"
          :value="reportContextButtonKey"
          :disabled="loadingReport"
          @insertRecommendation="handleInsertRecommendedContent"
        />
      </v-col>
    </v-row>
    <template v-if="loadingReport">
      <platform-spinner class="my-4 mx-auto" />
    </template>
    <PlatformWYSIWYG
      v-else
      :placeholder="tooltip"
      :initial-data="currentContent"
      :max-length="textFieldLimit"
      justified
      @newInput="saveUpdate"
    />

    <!-- AI Focus Areas Confirmation Dialog -->
    <platform-modal
      v-model="showAiConfirmDialog"
      :title="$t('analysis.ai.useAiContentTitle')"
      :width="600"
      icon="robot-outline"
    >
      <p>{{ $t('analysis.ai.replaceFocusAreasInstruction') }}</p>
      <div
        class="grey lighten-4 rounded mt-3 pa-3 pr-5 overflow-y-auto"
        :style="{ maxHeight: '300px' }"
      >
        <p class="text-body-2 mb-0 text-justify text-pre-wrap">{{ aiFocusAreas }}</p>
      </div>
      <p class="mt-3 text-caption warning--text">
        {{ $t('analysis.ai.replaceWarning') }}
      </p>
      <template #actions>
        <platform-button
          primary
          @click="useAiFocusAreas"
        >
          {{ $t('analysis.ai.replaceWithContentAction') }}
        </platform-button>
      </template>
    </platform-modal>
  </v-card>
</template>

<script lang="ts">
import Vue, { PropType } from 'vue'
import { mapActions } from 'vuex'
import { PlatformReport } from '@/types/Report'
import { MaxFieldLengths } from '@/helpers/validation'
import PlatformWYSIWYG from '@/components/PlatformWYSIWYG.vue'
import PlatformSpinner from '@/components/shared/PlatformSpinner.vue'
import PlatformButton from '@/components/shared/PlatformButton.vue'
import PlatformModal from '@/components/PlatformModal.vue'
import ReportContextButton from '@/components/analysis/ReportContextButton.vue'
import { ReportContextItem } from '@/helpers/recommendations'
import { parseMarkdownContent } from '@/helpers/text'
import { InputFields } from '@betterboards/shared/types/Platform'

export default Vue.extend({
  components: {
    ReportContextButton,
    PlatformSpinner,
    PlatformWYSIWYG,
    PlatformButton,
    PlatformModal
  },
  props: {
    report: { type: Object as PropType<PlatformReport>, required: false },
    loadingReport: { type: Boolean, default: false }
  },
  data: () => ({
    showAiConfirmDialog: false
  }),
  computed: {
    aiContent (): any {
      return this.$store.state.Analysis.aiContent
    },
    currentContent (): string | undefined {
      const focusAreas = this.report?.focusAreas
      if (focusAreas?.length) {
        return focusAreas ?? undefined
      }
      return undefined
    },
    aiFocusAreas (): string | undefined {
      if (!this.aiContent?.groupBySection) return undefined

      const sections = this.aiContent.groupBySection
      const focusAreaParts: string[] = []

      // Look for INTRO::ROOT entry
      const introSection = sections['INTRO::ROOT'] as any
      if (introSection?.focusArea) {
        focusAreaParts.push(introSection.focusArea)
      }

      // Only pick ROOT entries for focus areas
      Object.entries(sections).forEach(([sectionKey, section]: [string, any]) => {
        if (sectionKey !== 'INTRO::ROOT' && sectionKey.endsWith('::ROOT') && section?.focusArea) {
          focusAreaParts.push(section.focusArea)
        }
      })

      return focusAreaParts.length > 0 ? focusAreaParts.join('\n\n') : undefined
    },
    tooltip (): string {
      return this.$t('analysis.focusAreas.tooltip') as string
    },
    reportContextButtonKey (): ReportContextItem | undefined {
      return ReportContextItem.FocusAreas
    },
    textFieldLimit (): number {
      const focusAreasFieldOverride: number | undefined =
        this.report?.configuration?.maxFieldLengths?.[InputFields.FocusAreas]
      if (focusAreasFieldOverride) return focusAreasFieldOverride
      return MaxFieldLengths[InputFields.FocusAreas]
    }
  },
  methods: {
    ...mapActions('Report', ['saveReport']),
    async saveUpdate (val) {
      if (!this.report) {
        console.error('no report loaded')
        return
      }
      await this.saveReport({
        id: this.report.id,
        focusAreas: val
      })
    },
    handleInsertRecommendedContent (content: string): void {
      const newContent = this.currentContent
        ? `${this.currentContent}\n\n${content}`
        : content
      this.saveUpdate(parseMarkdownContent(newContent))
    },
    useAiFocusAreas (): void {
      if (!this.aiFocusAreas) return
      // Let the editor's CSS default handle justification; don't wrap in a styled <div>
      const formattedContent = parseMarkdownContent(this.aiFocusAreas)
      this.saveUpdate(formattedContent)
      this.showAiConfirmDialog = false
      this.$toasted.success(this.$t('analysis.aiAppliedToast') as string)
    }
  }
})
</script>

<style lang="scss" scoped>
.text-justify {
  text-align: justify;
}

.text-pre-wrap {
  white-space: pre-wrap;
}
</style>
